import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Eine Zeile fuer die Zone-Distribution-Anzeige.
 *
 * - `name`/`color`/`range` werden immer gerendert.
 * - `seconds`/`pct` sind optional: wenn gesetzt, wird der Fill-Bar +
 *   %/Zeit-Spalten angezeigt (mode='distribution').
 *   Wenn weggelassen, werden nur Range-Werte gezeigt (mode='bounds-only').
 */
export interface ZoneRowVm {
  name: string;
  color: string;
  range: string;
  seconds?: number;
  pct?: number;
}

export type ZoneDistributionMode = 'distribution' | 'bounds-only';

@Component({
  selector: 'app-zone-distribution',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="zone-dist" [class.zone-dist--bounds-only]="mode === 'bounds-only'">
      @for (z of rows; track z.name) {
        <div class="zone-dist__row">
          <span class="zone-dist__color" [style.background]="z.color"></span>
          <span class="zone-dist__name">{{ z.name }}</span>
          <span class="zone-dist__range">{{ z.range }}</span>
          @if (mode === 'distribution') {
            <div class="zone-dist__track">
              <div class="zone-dist__fill"
                   [style.width.%]="z.pct ?? 0"
                   [style.background]="z.color"></div>
            </div>
            <span class="zone-dist__pct">{{ z.pct ?? 0 }}%</span>
            <span class="zone-dist__time">{{ formatSeconds(z.seconds ?? 0) }}</span>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .zone-dist {
      display: grid;
      grid-template-columns: 12px 7rem 5.5rem 1fr 3rem 3.5rem;
      row-gap: 0.25rem;
      column-gap: 0.5rem;
      align-items: center;
      font-size: var(--fs-sm);
    }
    .zone-dist--bounds-only {
      grid-template-columns: 12px 9rem 1fr;
    }
    .zone-dist__row {
      display: contents;
    }
    .zone-dist__color {
      display: inline-block;
      width: 12px;
      height: 12px;
      border-radius: 3px;
    }
    .zone-dist__name {
      color: var(--text-primary, #222);
      font-weight: 500;
    }
    .zone-dist__range {
      color: var(--text-secondary, #666);
      font-variant-numeric: tabular-nums;
    }
    .zone-dist__track {
      position: relative;
      height: 8px;
      background: var(--surface-variant, #eee);
      border-radius: 4px;
      overflow: hidden;
    }
    .zone-dist__fill {
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      border-radius: 4px;
    }
    .zone-dist__pct,
    .zone-dist__time {
      text-align: right;
      color: var(--text-secondary, #666);
      font-variant-numeric: tabular-nums;
    }
  `],
})
export class ZoneDistributionComponent {
  @Input({ required: true }) rows: ZoneRowVm[] = [];
  @Input() mode: ZoneDistributionMode = 'distribution';

  protected formatSeconds(s: number): string {
    if (!s || s <= 0) return '0s';
    if (s < 60) return `${Math.round(s)}s`;
    const m = Math.floor(s / 60);
    const sec = Math.round(s - m * 60);
    return sec === 0 ? `${m}m` : `${m}m ${sec}s`;
  }
}
